import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';

import { FrontendAboutPage, FrontendAboutStat, FrontendAboutValue } from '../../../core/api/api.models';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { TrustedCmsHtmlPipe } from '../../../core/api/trusted-cms-html.pipe';
import { SeoService } from '../../../core/seo/seo.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { BigSaleBanner } from '../../../shared/big-sale-banner/big-sale-banner';

const ABOUT_PATH = '/about-us';
const DEFAULT_META_TITLE = 'About SavingArena | Verified Coupons & Honest Deals';
const DEFAULT_META_DESCRIPTION =
  'SavingArena helps millions of shoppers save with verified coupons, honest deals and cashback they can actually trust. Here is who we are and how we work.';
const DEFAULT_H1 = 'Smart shopping, made simple';
const DEFAULT_SHORT_DESCRIPTION =
  'SavingArena helps millions of shoppers save on the brands they love — with verified coupons, honest deals and cashback you can actually trust.';
const DEFAULT_CONTENT = `
  <h3>Our story</h3>
  <p>SavingArena started with a simple frustration: hunting for a working coupon should not feel like a part-time job. Too many sites list expired codes, fake discounts and confusing offers. We built a better way — a single, trusted place where every deal is checked, every store is real and every shopper saves more.</p>
  <p>Today our team curates thousands of coupons, promo codes and cashback offers across fashion, electronics, travel, groceries and everyday essentials. We remove expired codes automatically, add fresh ones daily, and keep the whole experience clean, fast and free.</p>
  <h3>Our mission</h3>
  <p>We believe saving money should be effortless and honest. Whether you are grabbing a last-minute deal or planning a big seasonal purchase, SavingArena gives you the verified information you need to buy with confidence — and keep more money in your pocket.</p>
`;
const DEFAULT_STATS: FrontendAboutStat[] = [
  { value: '50K+', label: 'Verified Coupons' },
  { value: '5,000+', label: 'Partner Stores' },
  { value: '1M+', label: 'Happy Shoppers' },
  { value: '$10M+', label: 'Saved & Counting' },
];
const DEFAULT_VALUES: FrontendAboutValue[] = [
  { title: 'Verified Daily', description: 'Our team checks codes every day, so you never waste time on offers that no longer work.' },
  { title: 'Always Free', description: 'No fees, no subscriptions, no catch — every coupon and deal on SavingArena is free to use.' },
  { title: 'Real Savings', description: 'We surface genuine discounts and cashback, not inflated “deals” designed to look bigger than they are.' },
  { title: 'Shopper First', description: 'A clean, fast and honest experience built entirely around what shoppers actually need.' },
];

@Component({
  selector: 'app-about-page',
  imports: [Breadcrumb, TrustedCmsHtmlPipe, BigSaleBanner],
  templateUrl: './about-page.html',
  styleUrl: './about-page.scss',
})
export class AboutPage {
  protected readonly crumbs = breadcrumbTrail({ label: 'About' });

  private readonly api = inject(SavingArenaApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);

  private readonly h1Override = signal('');
  private readonly shortDescriptionOverride = signal('');
  private readonly contentOverride = signal('');
  private readonly statsOverride = signal<FrontendAboutStat[]>([]);
  private readonly valuesOverride = signal<FrontendAboutValue[]>([]);

  protected readonly h1 = computed(() => this.h1Override() || DEFAULT_H1);
  protected readonly shortDescription = computed(
    () => this.shortDescriptionOverride() || DEFAULT_SHORT_DESCRIPTION,
  );
  protected readonly contentHtml = computed(() => this.contentOverride() || DEFAULT_CONTENT);
  protected readonly stats = computed(() =>
    this.statsOverride().length > 0 ? this.statsOverride() : DEFAULT_STATS,
  );
  protected readonly values = computed(() =>
    this.valuesOverride().length > 0 ? this.valuesOverride() : DEFAULT_VALUES,
  );

  constructor() {
    this.api
      .getAboutPageSettings()
      .pipe(
        catchError(() => of({ success: false, data: null })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.applySettings(response.data);
        // Applied even when the admin settings are missing, so the page always ships a
        // title/description/canonical rather than the app shell defaults.
        this.applySeo(response.data);
      });
  }

  private applySeo(settings: FrontendAboutPage | null | undefined): void {
    const title = (settings?.metaTitle ?? '').trim() || DEFAULT_META_TITLE;
    const description = (settings?.metaDescription ?? '').trim() || DEFAULT_META_DESCRIPTION;
    const url = this.seo.absolute(ABOUT_PATH);

    this.seo.apply({ title, description, canonicalPath: ABOUT_PATH });

    this.seo.setJsonLd([
      webPageSchema({ origin: this.seo.origin, url, name: title, description }),
      breadcrumbSchemaFor(this.crumbs, (p) => this.seo.absolute(p)),
    ]);
  }

  private applySettings(settings: FrontendAboutPage | null | undefined): void {
    if (!settings) {
      return;
    }

    this.h1Override.set((settings.h1 ?? '').trim());
    this.shortDescriptionOverride.set((settings.shortDescription ?? '').trim());
    this.contentOverride.set((settings.content ?? '').trim());

    const stats = Array.isArray(settings.stats)
      ? settings.stats
          .map((stat) => ({ value: (stat.value ?? '').trim(), label: (stat.label ?? '').trim() }))
          .filter((stat) => stat.value || stat.label)
      : [];
    this.statsOverride.set(stats);

    const values = Array.isArray(settings.values)
      ? settings.values
          .map((value) => ({ title: (value.title ?? '').trim(), description: (value.description ?? '').trim() }))
          .filter((value) => value.title || value.description)
      : [];
    this.valuesOverride.set(values);
  }
}
